<script lang="ts">
  import { onMount } from "svelte"
  import { formatDate, xhr, toast, blankOrValue } from "$app/utils"
  import InlineInput from "$app/components/InlineInput.svelte"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import DatePicker from "$app/components/DatePicker.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import _ from "lodash"
  import {
    ChevronRight,
    Newspaper,
    Lock,
    CircleAlert,
    ListCheck
  } from "lucide-svelte"

  import { isLeadership } from "$app/utils/goals.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import ToggleEdit from "$app/components/ToggleEdit.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    goalId: number
  }

  let { goalId }: Props = $props()

  const plan: PlanType = appStore.plan
  const current_user: UserType = appStore.current_user
  const editable: boolean = appStore.editable || appStore.update_goals

  const channel_id = appStore.channel_id

  let errors = {}

  let isLoading = $state(true)
  let goal: GoalType = $state()
  let departments: DepartmentType[] = $state([])
  let assignee = {}
  let tasks = []
  let annualGoals: GoalType[] = []
  let longTermGoals: GoalType[] = []

  let parentGoals: GoalType[] = $state([])

  let name = $state("")
  let description = $state("")
  let leadership_notes = $state("")
  let action_plan = $state("")
  let obstacles_text = $state("")

  let show_action_plan = $state(false)

  let show_options = $state(false)

  let expected_at = $state(null)
  let goal_year = null

  const parentTypes = {
    AnnualGoal: "LongTermGoal",
    QuarterlyGoal: "AnnualGoal"
  }

  async function initializeForm(): Promise<void> {
    isLoading = true
    try {
      const resp = await xhr.get(`/plans/${plan.id}/goals/${goalId}/edit.json`)

      goal = resp.data.goal
      name = goal.name
      description = goal.description
      leadership_notes = goal.leadership_notes
      action_plan = goal.action_plan
      obstacles_text = goal.obstacles_text
      checkActionPlan()

      departments = resp.data.departments
      try {
        expected_at = new Date(`${goal.expected_at}T00:00:00`)
        goal_year = expected_at.getFullYear()
      } catch (e) {}

      annualGoals = _.sortBy(
        resp.data.annual_goals as GoalType[],
        "assignee_position"
      )
      longTermGoals = _.sortBy(
        resp.data.long_term_goals as GoalType[],
        "assignee_position"
      )

      if (goal.type === "AnnualGoal") {
        parentGoals = longTermGoals
      } else if (goal.type === "QuarterlyGoal") {
        parentGoals = annualGoals
      }

      show_options = goal.type === "AnnualGoal" || goal.type === "QuarterlyGoal"

      assignee = departments.find((d) => d.value === resp.data.goal.assignee_id)

      tasks = resp.data.tasks
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the goal", "error")
    } finally {
      isLoading = false
    }
  }

  function checkActionPlan() {
    // Parse Action Plan to remove HTML Tags to see if it has actual text content
    let ap_html = action_plan
    let ap_div = document.createElement("div")
    ap_div.innerHTML = ap_html
    let ap_text = ap_div.textContent || ap_div.innerText || ""

    show_action_plan = ap_text.length > 0 || false
  }

  function updateParentGoal(): void {
    //handleSave({ parent_goal_id: goal.parent_goal_id })
    const parentGoal = parentGoals.find((g) => g.id === goal.parent_goal_id)
    goal.parent_goal_name = parentGoal.name
  }

  function updateStore(data: GoalType): void {
    const idx = appStore.goals.findIndex((g) => g.id === goal.id)
    appStore.goals[idx] = { ...goal, ...data }
    goal = { ...appStore.goals[idx] }
    try {
      expected_at = new Date(`${goal.expected_at}T00:00:00`)
    } catch (e) {}
  }

  let saving = $state(false)
  async function handleSave(
    payload: { [key: string]: any },
    field?: string
  ): Promise<void> {
    saving = true
    const prevGoal = { ...goal }
    try {
      goal = { ...goal, ...payload }
      const resp = await xhr.patch(
        `/plans/${plan.id}/goals/${goalId}.json?channel=${channel_id}`,
        {
          goal: payload
        }
      )
      goal = resp.data
      console.log(goal)
      updateStore(resp.data as GoalType)
    } catch (e) {
      goal = { ...prevGoal }
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast(`There was an error updating ${field ? field : ""}`, "error")
      if (e.response) {
        errors = { ...errors, ...e.response.data.errors }
      }
    } finally {
      saving = false
    }
  }

  let users: UserType[] = appStore.users
  let invalidUsers: UserType[] = $state([])
  let invalidVisibility = $state(false)
  const invalidUsersMsg =
    "There are members that do not belong to Leadership Team, please remove them in the Members tab."

  const goalsD = {
    AnnualGoal: { label: "Annual Goal", cls: "text-annual-goal" },
    QuarterlyGoal: { label: "Quarterly Goal", cls: "text-quarterly-goal" }
  }

  onMount(async () => {
    if (goal?.visibility === "leadership") {
      invalidUsers = goal.user_ids
        .map((u_id) => users.find((us) => us.id === u_id))
        .filter((u) => !isLeadership(u.role))

      if (invalidUsers.length > 0) {
        invalidVisibility = true
        toast(invalidUsersMsg, "error", { duration: 10000 })
      }
    } else {
      invalidVisibility = false
    }
    initializeForm()
  })
</script>

<div class="overflow-visible">
  <div>
    {#if isLoading}
      <LoadingSpinner />
    {:else}
      {@const goalD = goalsD[goal.type]}
      <div class="font-bold text-lg text-left {goalD.cls}">
        {goalD.label}
      </div>
      <div class="mb-3">
        {#if goal.parent_goal_type === "LongTermGoal" || goal.parent_goal_type === "PlanGoal"}
          <div
            class="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-long-term-goal"
          >
            Parent Long-Term Goal
            <ChevronRight class="text-gray-400 mx-2" />
            <span class="font-bold">{goal.parent_goal_name}</span>
          </div>
        {:else if goal.parent_goal_type === "AnnualGoal"}
          <div
            class="inline-flex items-center rounded bg-gray-50 px-2 py-0.5 text-xs font-medium text-annual-goal"
          >
            Parent Annual Goal
            <ChevronRight class="text-gray-400 mx-2" />
            <span class="font-bold">{goal.parent_goal_name}</span>
          </div>
        {/if}
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div
          class="col-span-12 {editable && show_options ? 'lg:col-span-10' : ''}"
        >
          <div class="font-medium text-gray-900 w-full pr-2">
            <label for="name" class="label text-sm text-gray-600"
              >Goal Name</label
            >
            {#if editable}
              <div class="text-lg">
                <InlineInput
                  bind:value={name}
                  postURL={`/plans/${plan.id}/goals/${goal.id}.json?channel=${channel_id}`}
                  name="name"
                  model="goal"
                />
              </div>
            {:else}
              <div
                class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
              >
                {name}
              </div>
            {/if}
          </div>
        </div>

        {#if (current_user.role === "leader" || current_user.role === "admin") && show_options}
          <div class="col-span-12 sm:col-span-6 lg:col-span-2">
            <label for="visibility" class="label text-sm text-gray-600"
              >Goal Visibility</label
            >
            {#if goal.parent_visibility == "plan"}
              <div class="input-cont">
                <select
                  id="visibility"
                  style={invalidVisibility ? "border: 1px solid red" : ""}
                  bind:value={goal.visibility}
                  onchange={(e) => console.log("visibilisty", e)}
                  title={invalidVisibility ? invalidUsersMsg : ""}
                >
                  <option value="plan">All Members</option>
                  <option value="leadership">Only Leadership Team</option>
                </select>
              </div>
            {:else}
              <div
                class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
              >
                Parent is Only Leadership Team
              </div>
            {/if}
          </div>
        {/if}
      </div>

      <div class="grid grid-cols-12 gap-6 mt-2">
        {#if show_options}
          <div class="col-span-6 lg:col-span-4">
            <label for="parent_goal_id" class="label text-sm text-gray-600"
              >Parent Goal</label
            >
            {#if editable}
              <div class="input-cont">
                <select
                  id="parent_goal_id"
                  bind:value={goal.parent_goal_id}
                  onchange={() => updateParentGoal()}
                >
                  {#if parentGoals.length}
                    {@const goalsForm = parentGoals.filter(
                      (g) => parentTypes[goal.type] === g.type
                    )}
                    {#each goalsForm as pg (pg.id)}
                      <option value={pg.id}>{pg.name}</option>
                    {:else}
                      <option value="">No Parent Goals for Expected By</option>
                    {/each}
                  {:else}
                    <option value="">No Parent Goals for Expected By</option>
                  {/if}
                </select>
              </div>
            {:else}
              <div
                class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
              >
                {goal.parent_goal_name}
              </div>
            {/if}
          </div>

          <div class="col-span-6 lg:col-span-4 lg:pl-4">
            <label for="status" class="label text-sm text-gray-600"
              >Assignee</label
            >
            <div class="mt-1 text-sm">
              <select
                id="assignee_id"
                value={goal.assignee_id}
                onchange={(e) => {
                  handleSave({ assignee_id: e.target.value }, "Assignee")
                }}
              >
                {#if departments.length}
                  {#each departments as dept (dept.id)}
                    <option value={dept.id}>{dept.name}</option>
                  {/each}
                {:else}
                  <option value="">No Departments</option>
                {/if}
              </select>
            </div>
          </div>
          <div class="col-span-6 lg:col-span-2">
            <label for="status" class="label text-sm text-gray-600"
              >Status</label
            >
            <EditGoalStatus
              goalOrTask={goal}
              type="Goal"
              {editable}
              onupdate={(g) => handleSave(g)}
            />
          </div>
          <div class="col-span-6 lg:col-span-2 relative">
            <label for="expected_at" class="label text-sm text-gray-600"
              >Expected By</label
            >
            {#if editable}
              <DatePicker
                date={goal.expected_at}
                onchange={(d) => handleSave({ expected_at: d })}
              />
            {:else}
              <div
                class="mt-1 text-sm border rounded border-gray-200 px-3 py-2"
              >
                {formatDate(goal.expected_at)}
              </div>
            {/if}
          </div>
        {/if}

        <div class="col-span-12 mt-2">
          <p class="flex items-center mb-1">
            <Newspaper class="text-gray-400 mr-2 h-4 w-4" />
            Description
          </p>

          <ToggleEdit
            onblur={() => {
              handleSave({ description: description }, "Description")
            }}
          >
            {#snippet editcont()}
              <div>
                <Tiptap
                  id="edit-description"
                  buttons={onlyTextButtons}
                  content={goal.description}
                  onupdate={({ html }) => (description = html)}
                />
              </div>
            {/snippet}
            {#snippet showcont()}
              <div class="border px-4 py-2 shadow-md ProseMirror">
                {@html blankOrValue(
                  description,
                  "Enter a description of this goal"
                )}
              </div>
            {/snippet}
          </ToggleEdit>
        </div>

        {#if current_user.role === "leader" || current_user.role === "admin"}
          <div class="col-span-12 mb-4">
            <h3 class="flex items-center mb-1">
              <Lock class="text-gray-400 mr-2 h-4 w-4" />
              Leadership Notes
            </h3>
            <ToggleEdit
              onblur={() => {
                handleSave(
                  { leadership_notes: leadership_notes },
                  "Leadership Notes"
                )
              }}
            >
              {#snippet editcont()}
                <div>
                  <Tiptap
                    id="edit-leadership-notes"
                    buttons={onlyTextButtons}
                    content={goal.leadership_notes}
                    onupdate={({ html }) => (leadership_notes = html)}
                  />
                </div>
              {/snippet}
              {#snippet showcont()}
                <div class="border px-4 py-2 shadow-md ProseMirror">
                  {@html blankOrValue(
                    leadership_notes,
                    "Enter any private notes here which are visible only for Leadership Team members."
                  )}
                </div>
              {/snippet}
            </ToggleEdit>
          </div>
        {/if}
        {#if show_action_plan}
          <div class="col-span-12">
            <p class="flex items-center mb-1">
              <ListCheck class="text-gray-400 mr-2 h-4 w-4" />
              Action Plan
            </p>
            <ToggleEdit
              onblur={() =>
                handleSave({ action_plan: action_plan }, "Action Plan")}
            >
              {#snippet editcont()}
                <div>
                  <Tiptap
                    id="edit-action-plan"
                    buttons={onlyTextButtons}
                    content={goal.action_plan}
                    onupdate={({ html }) => {
                      action_plan = html
                    }}
                  />
                </div>
              {/snippet}
              {#snippet showcont()}
                <div class="border px-4 py-2 shadow-md ProseMirror">
                  {@html blankOrValue(
                    action_plan,
                    "Enter your action plan for accomplishing this goal"
                  )}
                </div>
              {/snippet}
            </ToggleEdit>
          </div>
        {/if}
        <div class="col-span-12">
          <p class="flex items-center mb-1">
            <CircleAlert class="text-gray-400 mr-2 h-4 w-4" />
            Obstacles
          </p>
          <ToggleEdit
            onblur={() => {
              handleSave({ obstacles_text: obstacles_text }, "Obstacles")
            }}
          >
            {#snippet editcont()}
              <div>
                <Tiptap
                  id="edit-obstacles-text"
                  buttons={onlyTextButtons}
                  content={goal.obstacles_text}
                  onupdate={({ html }) => (obstacles_text = html)}
                />
              </div>
            {/snippet}
            {#snippet showcont()}
              <div class="border px-4 py-2 shadow-md ProseMirror">
                {@html blankOrValue(
                  obstacles_text,
                  "Enter any obstacles to accomplishing this goal"
                )}
              </div>
            {/snippet}
          </ToggleEdit>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  :root {
    --toastHeight: auto;
  }
</style>
